import { Card, CardActionArea, CardContent, Typography, Box } from '@mui/material';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import React, { useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useTheme } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';

type InfoCardProps = {
  value: number;
  errorCode: string;
  total?: number;
  getColor: (errorCode: string, theme: any) => string;
  getBackground?: (errorCode: string, theme: any) => string;
  translationKey: string;
  unitLabel: string;
  Icon?: React.ElementType;
  dispatchAction?: (payload: string) => any;
  selectedFilterSelector?: (state: any) => string | string[] | undefined;
};

const InfoCard = ({
  value,
  errorCode,
  total,
  getColor,
  getBackground,
  translationKey,
  unitLabel,
  Icon,
  dispatchAction,
  selectedFilterSelector,
}: InfoCardProps) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const fallbackSelector = (state: any) => undefined as string | undefined;
  const selectedValue = useSelector(selectedFilterSelector ?? fallbackSelector);
  const isActive = Array.isArray(selectedValue)
    ? selectedValue.includes(errorCode)
    : selectedValue === errorCode;

  const color = getColor(errorCode, theme);
  const backgroundColor = getBackground ? getBackground(errorCode, theme) : undefined;
  const label: string = t(`${translationKey}.${errorCode}`);
  const percent: number = useMemo(() => (total ? (100 * value) / total : 0), [value, total]);

  const handleToggle = () => {
    if (!dispatchAction) return;
    dispatch(dispatchAction(errorCode));
  };

  const isClickable = Boolean(dispatchAction);

  const cardContent = (
    <CardContent
      sx={{
        px: 1.5,
        pt: { xs: 1, md: 1, lg: 1.2, xl: 2 },
        height: '100%',
        '&:last-child': {
          paddingBottom: {
            xs: theme.spacing(1),
            md: theme.spacing(1),
            lg: theme.spacing(1.2),
            xl: theme.spacing(2),
          },
        },
        display: 'grid',
        gridTemplateRows: 'auto 1fr auto',
        rowGap: 0.15,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center' }}>
        {Icon ? (
          <Icon sx={{ color, backgroundColor, borderRadius: '10%' }} />
        ) : (
          <Box sx={{ backgroundColor: color, width: 12, height: 12, borderRadius: '50%', mr: 1 }} />
        )}

        <Typography variant="body1" sx={{ fontWeight: 500, ...(Icon ? { ml: 1 } : {}) }}>
          {label}
        </Typography>

        {isClickable &&
          (isActive ? (
            <CheckBoxIcon
              sx={{ ml: 'auto', fontSize: 18, color, transition: 'color 0.15s ease' }}
            />
          ) : (
            <CheckBoxOutlineBlankIcon
              sx={{
                ml: 'auto',
                fontSize: 18,
                color: 'text.disabled',
                transition: 'color 0.15s ease',
              }}
            />
          ))}
      </Box>
      <Box sx={{ alignSelf: 'end', display: 'flex', alignItems: 'baseline', gap: 1.5 }}>
        <Typography variant="h5" fontWeight="bold" display="block">
          {value}
        </Typography>

        <Typography variant="body2" color="text.secondary">
          {unitLabel}
        </Typography>
      </Box>
      <Box
        mt={0}
        borderRadius={2}
        sx={{
          backgroundColor: theme.custom.progressBarBackground,
          height: 6,
          position: 'relative',
        }}
      >
        <Box
          sx={{
            backgroundColor: color,
            width: `${percent}%`,
            height: '100%',
            borderRadius: 2,
          }}
        />
      </Box>
    </CardContent>
  );

  return (
    <Card
      sx={{
        flex: 1,
        borderRadius: 3,
        boxShadow: isActive ? 4 : 2,
        mt: 1,
        maxWidth: '250px',
        background: theme.palette.background.paper,
        height: { lg: '12.5vh', xl: '12.5vh' },
        minHeight: '85px',
        transition: 'box-shadow 0.15s ease',
      }}
    >
      {isClickable ? (
        <CardActionArea
          onClick={handleToggle}
          sx={{ height: '100%', '&:hover .MuiSvgIcon-root': { color } }}
        >
          {cardContent}
        </CardActionArea>
      ) : (
        cardContent
      )}
    </Card>
  );
};

export default InfoCard;
